import React from 'react';
import { useSelector } from 'react-redux';

import FileLink from 'components/general/fileLink';
import Files from 'components/admin/forms/files';
import Assets, { assetLink } from 'components/general/assets';
import E4Player from 'components/general/e4Player';
import Insights from '../insights';
import Video from './components/video';
import ScreenRecorder from './components/screenRecorder';
import MetricGraph from './components/metricGraph';
import MandatoryAssets from '../mandatoryAssets';

import { Copy, Forms, ProgressBar, SystemText } from 'e4shared';

import config from '../../../../../config';
import { uploadType } from 'helpers/azure';

export const TaskCommentSection = ({
    task,
    profile,
    comments,
    assets,
    video,
    setVideo,
    extraMessage,
    clearShowSuccess,
    showSuccess,
    canFeedback,
    progress,
    mandatoryAssets,
    setPageComments,
    setPageVideo,
    setPageAssets,
    commentsError,
    assetError,
}) => {
    const [pitchbackType, setPitchbackType] = React.useState('webcam');

    const { handles, tags } = useSelector(state => state.ui);
    const user = useSelector(state => state.user.user);

    const st = (path, ...rest) => SystemText(`Objective/Pitchback/${path}`, ...rest);

    const showVideo = task.completed_Video_Asset;
    const isMe = profile?.id === user.id;

    const fileAsset = {
        ...task.completed_Video_Asset,
        video: task.completed_Video_Asset !== null,
    };

    const aiFeedbackSection = () => {
        if (!task.challenge_Id) return null;

        if (task.video?.insights && Object.keys(task.video?.insights).length && !task.video?.insights?.transcript?.length) {
            return (
                <div className="ai-feedback">
                    <div className="flex flex-center mb5">
                        <i className="fa fa-sparkles mr10" />
                        <div>
                            <b className="text-secondary-berry">{st('AI/NoTranscript')}</b>
                            <div className="mt10">
                                <Copy value={st('AI/NoTranscript/Body')} />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (task.aI_Feedback) {
            let aiParsed = {};

            try {
                aiParsed = JSON.parse(task.aI_Feedback);
            } catch (error) {
                console.error('Failed to parse AI Feedback:', error);
            }

            const { positive, corrective } = aiParsed?.evaluation || {};

            return (
                <>
                    <div className="ai-feedback">
                        <div className="flex flex-center">
                            <i className="fa fa-sparkles mr10" />
                            <b>{st('AI/Feedback')}</b>
                        </div>
                    </div>
                    <div className="mb10">
                        <div>
                            <label className="mr5">{st('AI/Feedback/Strengths')}</label>
                        </div>
                        <Copy value={positive || '-'} />
                    </div>
                    <div className="mb10">
                        <div>
                            <label className="mr5">{st('AI/Feedback/Opportunities')}</label>
                        </div>
                        <Copy value={corrective || '-'} />
                    </div>
                </>
            );
        }

        return (
            <div className="ai-feedback">
                <div className="flex flex-center mb5">
                    <i className="fa fa-sparkles mr10" />
                    <b>{st('AI/Awaiting')}</b>
                </div>
                <div>{st('AI/Awaiting/Body')}</div>
            </div>
        );
    };

    const completeSection = (
        <div className="form-group">
            <div className="user-comment">
                <div className="row">
                    <div className="col-md-12">
                        {canFeedback && !isMe && <div className="mb10 text-red">{st('Approve/Manager')}</div>}

                        {task.lms_Task && mandatoryAssets?.length > 0 && (
                            <div className="form-group">
                                {/* <label>{st('Edit/Completed')}</label> */}
                                <MandatoryAssets assets={mandatoryAssets} linkToLms={isMe} />
                            </div>
                        )}

                        {task.metric_Task && task.date_Completed && (
                            <div className="form-group">{<MetricGraph task={task} />}</div>
                        )}

                        {showVideo && (
                            <span>
                                <label className="mr5">{st('Label')}</label>
                                <div>
                                    {task.completed_Video_Asset.source === 'file' ? (
                                        <FileLink folder="task_Completion" file={fileAsset} useVideoPosition />
                                    ) : task.completed_Video_Asset.source === 'gong' ? (
                                        <div>
                                            <a
                                                href={assetLink(task.completed_Video_Asset)}
                                                target="noopener noreferrer"
                                                rel="noreferrer"
                                            >
                                                {task.completed_Video_Asset.name}
                                            </a>
                                        </div>
                                    ) : (
                                        <>
                                            <E4Player asset={task.completed_Video_Asset} progress={0} video allow_FF />
                                        </>
                                    )}
                                </div>
                            </span>
                        )}

                        {task.video?.insights && <Insights profileId={profile.id} task={task} />}

                        {aiFeedbackSection()}

                        {task.comments && (
                            <div className={showVideo ? 'mt10' : 'mb10'}>
                                <div>
                                    <label className="mr5">{st('Notes')}</label>
                                </div>
                                <Copy value={task.comments ? task.comments : '-'} />
                            </div>
                        )}

                        {task.completed_Assets && (
                            <div className="mt10">
                                <label className="mr5">{st('Extra')}</label>
                                <Assets assets={task.completed_Assets} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const completeShowSuccessSection = (
        <div className="form-group">
            <div>
                <i className="fa fa-check-circle text-success mr10" />
                {st('Completed')}
            </div>
            {aiFeedbackSection()}
            <button data-testid="button-review" className="btn btn-default mt20" onClick={() => clearShowSuccess()}>
                {st('Buttons/ReviewComments')}
            </button>
        </div>
    );

    const showSuccessSection = (
        <div className="form-group">
            <div>
                <i className="fa fa-check-circle text-success mr10" />
                {st('DraftSaved')}
            </div>
            <button data-testid="button-continue" className="btn btn-default mt20" onClick={() => clearShowSuccess()}>
                {st('Buttons/Continue')}
            </button>
        </div>
    );

    const notUserSection = (
        <div className="form-group">
            {task.metric_Task && (
                <div className="form-group">
                    <MetricGraph task={task} />
                </div>
            )}
            <div className="user-comment italic">{st('Edit/Waiting', profile?.full_Name)}</div>
        </div>
    );

    const editSection = (
        <div className="row">
            <div className="col-md-12">
                {task.metric_Task && (
                    <div className="form-group">
                        <MetricGraph task={task} />
                    </div>
                )}

                {(task.lms_Task || mandatoryAssets?.length > 0) && (
                    <div className="mb10">
                        {mandatoryAssets.filter(a => !a.completion?.completed_At).length > 0 && (
                            <div className="flex mb5">
                                <i
                                    className="fa fa-warning text-danger text-lg  mr10"
                                    style={{ position: 'relative', top: '2px' }}
                                />
                                <span className="flex-grow">
                                    <div className="mb20">
                                        {SystemText('Objective/Lessons/Before')}
                                        {task.learning360 || task.lessonly ? (
                                            <span className="no-wrap">
                                                {' '}
                                                [
                                                <button
                                                    className="btn btn-link"
                                                    onClick={() => api.loadTask(id, taskId, true)}
                                                >
                                                    {SystemText('Objective/Buttons/Refresh')}
                                                </button>
                                                ]:
                                            </span>
                                        ) : (
                                            <span>:</span>
                                        )}
                                    </div>
                                </span>
                            </div>
                        )}
                        <MandatoryAssets assets={mandatoryAssets} extraMessage={extraMessage} />
                    </div>
                )}

                {task.video && (
                    <div>
                        <Forms.Buttons
                            data-testid="buttons-video"
                            error={assetError}
                            label={st('Fields/Video/Label')}
                            mandatory
                            forceHelp
                            help={st('Fields/Video/Help')}
                        >
                            <div className="flex">
                                <button
                                    data-testid="button-webcam"
                                    className={`flex-33 btn btn-${pitchbackType === 'webcam' ? 'primary' : 'default'}`}
                                    onClick={() => setPitchbackType('webcam')}
                                >
                                    <i className="fa fa-face-viewfinder mr5" />
                                    {st('Fields/Video/Webcam')}
                                </button>
                                <button
                                    data-testid="button-screen"
                                    className={`flex-grow btn btn-${pitchbackType === 'screen' ? 'primary' : 'default'}`}
                                    onClick={() => setPitchbackType('screen')}
                                >
                                    <i className="fa fa-laptop mr5" />
                                    {st('Fields/Video/Screen')}
                                </button>
                                <button
                                    data-testid="button-file"
                                    className={`flex-33 btn btn-${pitchbackType === 'file' ? 'primary' : 'default'}`}
                                    onClick={() => setPitchbackType('file')}
                                >
                                    <i className="fa fa-circle-up mr5" />
                                    {st('Fields/Video/File')}
                                </button>
                            </div>
                        </Forms.Buttons>
                        <div className="">
                            {pitchbackType === 'webcam' && (
                                <Video
                                    label={st('Fields/Webcam/Label')}
                                    folder="task_Completion"
                                    value={video && video.source === 'file' ? video : null}
                                    onChange={value => setPageVideo(value)}
                                    fileName={`practice-wc-${profile?.id}-${task.id}`}
                                    fullWidth
                                />
                            )}
                            {pitchbackType === 'screen' && (
                                <ScreenRecorder
                                    label={st('Fields/Screen/Label')}
                                    folder="task_Completion"
                                    value={video}
                                    onChange={value => setVideo(value)}
                                    fileName={`practice-sr-${profile?.id}-${task.id}`}
                                    forceUpdate={() => {
                                        setPitchbackType('webcam');
                                        setPitchbackType('screen');
                                    }}
                                />
                            )}
                            {pitchbackType === 'file' && (
                                <Files
                                    value={video}
                                    extensions={config.mediaExtensions}
                                    maxSize={config.MAX_VIDEO_SIZE}
                                    onChange={setVideo}
                                    folder={uploadType.taskCompletion}
                                    allowMultiple={false}
                                />
                            )}
                            {pitchbackType !== 'file' && video && (
                                <div className="flex flex-center mb10 files-displayed p10">
                                    <i className="fa fa-file-movie mr5" />
                                    <span className="flex-grow">{video.name}</span>
                                    <span className="ml5">
                                        <span
                                            className="pointer"
                                            title={st('Fields/File/Delete')}
                                            onClick={() => setVideo(null)}
                                        >
                                            <i className="fa fa-trash text-danger" />
                                        </span>
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {task.rejections.length > 0 && !task.approved && (
                    <Forms.Label label={st('Rejected/Label')} value={st('Rejected/Summary')} error />
                )}

                <div className={task.video ? 'mt20' : ''} />
                <Forms.Textarea
                    data-testid="text-comments"
                    tags={tags}
                    handles={handles}
                    label={st('Fields/Notes/Label')}
                    mandatory="true"
                    error={commentsError}
                    rows="5"
                    value={comments}
                    onChange={setPageComments}
                    placeholder={st('Fields/Notes/Placeholder')}
                />
                <Files
                    value={assets}
                    onChange={value => setPageAssets(value)}
                    label={st('Fields/Extra/Label')}
                    help={st('Fields/Extra/Help')}
                    folder={uploadType.taskCompletion}
                    allowMultiple
                />
                {progress > 0 && <ProgressBar title="Uploading video" small percent progress={progress} />}
            </div>
        </div>
    );

    if (task.date_Completed && showSuccess && user.id === profile?.id) {
        return <span id="task-comment-section">{completeShowSuccessSection}</span>;
    }

    if (task.date_Completed) {
        return <span id="task-comment-section">{completeSection}</span>;
    }

    if (user.id !== profile?.id) {
        return <span id="task-comment-section">{notUserSection}</span>;
    }

    return <span id="task-comment-section">{showSuccess ? showSuccessSection : editSection}</span>;
};

export default TaskCommentSection;
